import { useState, useRef, useEffect } from "react";
import {
  PlusIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/solid";
import { formatCurrency } from "helpers/currency";
import type {
  EngravingOptions,
  EngravingFontSize,
  EngravingFont,
} from "./types";
import {
  ENGRAVING_PRICE,
  ENGRAVING_MAX_LINES,
  ENGRAVING_MAX_CHARS,
  ENGRAVING_FONT_SIZES,
  ENGRAVING_FONTS,
  ENGRAVING_SYMBOLS,
  createDefaultEngraving,
  DEFAULT_ENGRAVING_LINE,
} from "./engravingConstants";

type EngravingOptionsFormProps = {
  engraving: EngravingOptions | null;
  onChange: (engraving: EngravingOptions | null) => void;
  readOnly?: boolean;
  includedInPlan?: boolean;
};

export default function EngravingOptionsForm({
  engraving,
  onChange,
  readOnly = false,
  includedInPlan = false,
}: EngravingOptionsFormProps) {
  const isEnabled = !!engraving;

  const handleToggle = () => {
    if (readOnly) return;
    onChange(isEnabled ? null : createDefaultEngraving());
  };

  const updateLine = (
    index: number,
    updates: Partial<{ text: string; fontSize: EngravingFontSize }>,
  ) => {
    if (!engraving) return;
    const lines = engraving.lines.map((line, i) =>
      i === index ? { ...line, ...updates } : line,
    );
    onChange({ ...engraving, lines });
  };

  const addLine = () => {
    if (!engraving || engraving.lines.length >= ENGRAVING_MAX_LINES) return;
    onChange({
      ...engraving,
      lines: [...engraving.lines, { ...DEFAULT_ENGRAVING_LINE }],
    });
  };

  const removeLine = (index: number) => {
    if (!engraving || engraving.lines.length <= 1) return;
    onChange({
      ...engraving,
      lines: engraving.lines.filter((_, i) => i !== index),
    });
  };

  const setFont = (font: EngravingFont) => {
    if (!engraving) return;
    onChange({ ...engraving, font });
  };

  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const fontDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        fontDropdownRef.current &&
        !fontDropdownRef.current.contains(e.target as Node)
      ) {
        setFontDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedFontConfig = ENGRAVING_FONTS.find(
    (f) => f.value === engraving?.font,
  );

  const toggleSymbol = (symbolId: string) => {
    if (!engraving) return;
    onChange({
      ...engraving,
      symbolId: engraving.symbolId === symbolId ? null : symbolId,
    });
  };

  return (
    <div className="border-t border-oat pt-6">
      {/* Toggle */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={readOnly}
        className="flex w-full items-center gap-3 pl-0"
      >
        <div
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
            isEnabled ? "bg-evergreen" : "bg-gray-300"
          }`}
        >
          <div
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
              isEnabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </div>
        <div>
          <span className="font-semibold text-evergreen">Add engraving</span>
          <span className="ml-2 text-sm text-soil">
            {includedInPlan
              ? "(Included in plan)"
              : `(+${formatCurrency(ENGRAVING_PRICE)})`}
          </span>
        </div>
      </button>

      {/* Engraving options */}
      {isEnabled && engraving && (
        <div className="mt-6 space-y-6">
          {/* Font selection */}
          <div ref={fontDropdownRef} className="relative">
            <label className="mb-2 block text-sm font-semibold text-evergreen">
              Font
            </label>
            <button
              type="button"
              onClick={() => !readOnly && setFontDropdownOpen((o) => !o)}
              disabled={readOnly}
              className="flex w-full items-center justify-between rounded-md border border-oat bg-white px-3 py-2 text-left text-soil focus:border-evergreen focus:outline-none focus:ring-1 focus:ring-evergreen"
            >
              <span
                style={{
                  fontFamily: selectedFontConfig?.fontFamily,
                  fontStyle: selectedFontConfig?.italic ? "italic" : "normal",
                }}
                className="text-lg"
              >
                {engraving.font}
              </span>
              <ChevronDownIcon className="h-4 w-4 text-soil" />
            </button>
            {fontDropdownOpen && (
              <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-oat bg-white shadow-lg">
                {ENGRAVING_FONTS.map((f) => (
                  <li key={f.value}>
                    <button
                      type="button"
                      onClick={() => {
                        setFont(f.value);
                        setFontDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2.5 text-left text-lg hover:bg-oat/50 ${
                        engraving.font === f.value ? "bg-oat" : ""
                      }`}
                      style={{
                        fontFamily: f.fontFamily,
                        fontStyle: f.italic ? "italic" : "normal",
                      }}
                    >
                      {f.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Lines */}
          <div className="space-y-4">
            {engraving.lines.map((line, index) => (
              <div key={index}>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-semibold text-evergreen">
                    {index === 0 ? "Name" : `Line ${index + 1}`}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-soil">
                      {ENGRAVING_MAX_CHARS - line.text.length} characters left
                    </span>
                    {engraving.lines.length > 1 && !readOnly && (
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        className="text-soil/50 hover:text-soil"
                        aria-label={`Remove line ${index + 1}`}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                <input
                  type="text"
                  value={line.text}
                  onChange={(e) =>
                    updateLine(index, {
                      text: e.target.value.slice(0, ENGRAVING_MAX_CHARS),
                    })
                  }
                  disabled={readOnly}
                  placeholder={index === 0 ? "Name" : `Line ${index + 1} text`}
                  maxLength={ENGRAVING_MAX_CHARS}
                  className="w-full rounded-md border border-oat bg-white px-3 py-2 text-soil placeholder:text-soil/40 focus:border-evergreen focus:outline-none focus:ring-1 focus:ring-evergreen"
                />

                {/* Font size radio pills */}
                <div className="mt-2 flex gap-1">
                  {ENGRAVING_FONT_SIZES.map((size) => (
                    <button
                      key={size.value}
                      type="button"
                      onClick={() =>
                        !readOnly && updateLine(index, { fontSize: size.value })
                      }
                      disabled={readOnly}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                        line.fontSize === size.value
                          ? "bg-evergreen text-white"
                          : "border border-oat text-soil hover:bg-oat"
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {engraving.lines.length < ENGRAVING_MAX_LINES && !readOnly && (
              <button
                type="button"
                onClick={addLine}
                className="flex items-center gap-1 text-sm font-semibold text-evergreen hover:underline"
              >
                <PlusIcon className="h-4 w-4" />
                Add another line
              </button>
            )}
          </div>

          {/* Symbol grid */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-evergreen">
              Symbol <span className="font-normal text-soil">(optional)</span>
            </label>
            <div className="grid grid-cols-6 gap-2 sm:grid-cols-8">
              {ENGRAVING_SYMBOLS.map((symbol) => (
                <button
                  key={symbol.id}
                  type="button"
                  onClick={() => !readOnly && toggleSymbol(symbol.id)}
                  disabled={readOnly}
                  title={symbol.name}
                  className={`flex h-12 w-12 items-center justify-center rounded-md bg-evergreen/80 p-1.5 transition-all ${
                    engraving.symbolId === symbol.id
                      ? "ring-2 ring-evergreen ring-offset-1"
                      : "opacity-70 hover:opacity-100"
                  }`}
                >
                  <img
                    src={symbol.image}
                    alt={symbol.name}
                    className="h-full w-full object-contain"
                  />
                </button>
              ))}
            </div>
            {engraving.symbolId && !readOnly && (
              <button
                type="button"
                onClick={() => onChange({ ...engraving, symbolId: null })}
                className="mt-2 text-sm text-soil hover:underline"
              >
                Remove symbol
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
